import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/context/LanguageContext";
import { TESTIDS } from "@/constants/testIds";
import { requestPasswordReset } from "@/lib/api";

const AuthModal = ({ open, onClose, defaultTab = "login" }) => {
  const { t } = useT();
  const [tab, setTab] = useState(defaultTab);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [resetSent, setResetSent] = useState(false);
  const { login, register, startGoogleLogin } = useAuth();

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      if (tab === "login") await login(email.trim(), password);
      else await register(name.trim() || "Friend of the House", email.trim(), password);
      onClose?.();
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async () => {
    if (!email.trim()) {
      setError("Enter your email above, then click 'Forgot password?' again.");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      await requestPasswordReset(email.trim());
      setResetSent(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn't send reset email.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[120] bg-[#2C2C2C]/30 backdrop-blur-md fade-in flex items-center justify-center px-4" data-testid={TESTIDS.authModal}>
      <div className="bg-[#F9F8F5] w-full max-w-md p-8 md:p-10 relative drawer-anim">
        <button data-testid={TESTIDS.authClose} onClick={onClose} className="absolute top-4 right-4 text-xl font-thin">✕</button>

        <div className="font-typewriter text-[10px] tracking-[0.32em] uppercase text-[#6B6862]">{t("auth.eyebrow")}</div>
        <h2 className="font-serif-display text-3xl mt-2 mb-6 leading-tight">
          {tab === "login" ? t("auth.welcome") : t("auth.join")}
        </h2>

        <div className="flex gap-6 mb-6 border-b border-[#E3DFD7]">
          <button
            data-testid={TESTIDS.authTabLogin}
            onClick={() => setTab("login")}
            className={`pb-3 font-typewriter text-[11px] tracking-[0.22em] uppercase ${tab === "login" ? "text-[#2C2C2C] border-b border-[#2C2C2C]" : "text-[#6B6862]"}`}
          >{t("auth.signin")}</button>
          <button
            data-testid={TESTIDS.authTabRegister}
            onClick={() => setTab("register")}
            className={`pb-3 font-typewriter text-[11px] tracking-[0.22em] uppercase ${tab === "register" ? "text-[#2C2C2C] border-b border-[#2C2C2C]" : "text-[#6B6862]"}`}
          >{t("auth.create")}</button>
        </div>

        {/* Google — primary path. REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
        <button
          type="button"
          data-testid={TESTIDS.authGoogleBtn}
          onClick={startGoogleLogin}
          className="w-full flex items-center justify-center gap-3 border border-[#241C18]/15 bg-white hover:bg-[#FFF8E9] transition rounded-full py-3 px-4 mb-5"
        >
          <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
            <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.5 5.9 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/>
            <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.7 19 12.5 24 12.5c3 0 5.8 1.1 7.9 3l5.7-5.7C34.5 5.9 29.6 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
            <path fill="#4CAF50" d="M24 44c5.5 0 10.5-2.1 14.3-5.6l-6.6-5.4c-2 1.4-4.6 2.3-7.7 2.3-5.2 0-9.7-3.3-11.3-8L6 32.5C9.4 39 16.1 44 24 44z"/>
            <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.2 5.7l6.6 5.4C41.6 36.6 44 30.9 44 24c0-1.2-.1-2.3-.4-3.5z"/>
          </svg>
          <span className="font-sans-clean text-sm text-[#241C18]">
            {tab === "login" ? "Continue with Google" : "Sign up with Google"}
          </span>
        </button>

        <div className="flex items-center gap-3 mb-5">
          <div className="flex-1 h-px bg-[#E3DFD7]" />
          <span className="font-typewriter text-[10px] tracking-[0.28em] uppercase text-[#8A7568]">or</span>
          <div className="flex-1 h-px bg-[#E3DFD7]" />
        </div>

        <form onSubmit={submit} className="space-y-5">
          {tab === "register" && (
            <input data-testid={TESTIDS.authName} className="input-editorial" placeholder={t("auth.namePh")} value={name} onChange={(e) => setName(e.target.value)} required />
          )}
          <input data-testid={TESTIDS.authEmail} type="email" className="input-editorial" placeholder={t("auth.emailPh")} value={email} onChange={(e) => setEmail(e.target.value)} required />
          <input data-testid={TESTIDS.authPassword} type="password" className="input-editorial" placeholder={t("auth.passwordPh")} value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} />

          {error && <div className="text-sm text-[#C97A63] font-typewriter tracking-wider">{error}</div>}
          {resetSent && (
            <div className="text-sm text-[#241C18] bg-[#FFF3E6] rounded-lg px-3 py-2" data-testid="auth-reset-sent">
              If that email exists, we've just sent you a reset link. Check your inbox.
            </div>
          )}

          <button data-testid={TESTIDS.authSubmit} type="submit" disabled={loading} className="btn-ink w-full">
            {loading ? t("auth.loading") : tab === "login" ? t("auth.submitSignin") : t("auth.submitCreate")}
          </button>

          {tab === "login" && !resetSent && (
            <button
              type="button"
              onClick={handleForgot}
              data-testid="auth-forgot-btn"
              className="w-full text-center text-xs font-badge tracking-[0.2em] uppercase text-[#8A7568] underline underline-offset-4 hover:text-[var(--gl-orange-hover)]"
            >
              Forgot password?
            </button>
          )}
        </form>

        <p className="mt-6 text-xs font-sans-clean text-[#6B6862] italic leading-relaxed">{t("auth.foot")}</p>
      </div>
    </div>
  );
};

export default AuthModal;
